import { KeyboardEvent, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Chip-style interests editor. Serializes to the same comma-separated
 * string the backend already stores in the `interest` field, so it is a
 * drop-in replacement for the free-text Textarea it supersedes.
 */
interface InterestsInputProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const parseInterests = (value?: string): string[] =>
  (value || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

export const InterestsInput = ({
  value,
  onChange,
  disabled,
  placeholder = 'Add an interest...',
}: InterestsInputProps) => {
  const [draft, setDraft] = useState('');
  const items = parseInterests(value);

  const commit = (raw: string) => {
    const next = raw.trim().replace(/,+$/, '');
    if (!next) return;
    if (items.some((i) => i.toLowerCase() === next.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...items, next].join(', '));
    setDraft('');
  };

  const removeAt = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx).join(', '));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && items.length > 0) {
      removeAt(items.length - 1);
    }
  };

  return (
    <div
      className={`flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 focus-within:ring-1 focus-within:ring-ring ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      {items.map((item, idx) => (
        <span
          key={`${item}-${idx}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2.5 py-1 text-sm font-medium text-secondary-foreground"
        >
          {item}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Remove ${item}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      ))}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(draft)}
        placeholder={placeholder}
        className="min-w-[140px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
    </div>
  );
};

export default InterestsInput;
